import { Formik, FormikHelpers, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import Button from '../../../common/button/button.component';
import { ParcelItem, HsCodeResponse } from '../../../models/hsCode';
import { GatewayCategoryDto } from '../../../models/gateway-category';
import {
  AiReviewControls,
  ConditionOption,
  ConditionSelector,
  FormBtnsBlock,
  FormInputs,
  ParcelItemEditFormContainer,
} from './parcel-items-management.styles';
import FormFieldText from '../../../common/form-field-text/form-field-text.component';
import GatewayCategorySearch from './gateway-category-search.component';
import InputMessage from '../../../common/input-message/input-message.component';
import { useStore } from '../../../stores/store';
import { useEditForm } from './edit-form-manager';

export interface ParcelItemFormValues {
  foundHsCode?: HsCodeResponse;
  category?: string;
  categoryElement?: GatewayCategoryDto;
  quantity?: number;
  declaredValue?: number;
  descriptionFree?: string;
  imei?: string;
  is_new?: boolean;
  is_ai_generated?: boolean;
}
interface Props {
  parcelItem?: ParcelItem;
  defaultValues?: ParcelItemFormValues;
  displayCancelButton: boolean;
  currencySymbol: string;
  allowUnmodifiedSubmit?: boolean;
  aiReviewProgress?: {
    current: number;
    total: number;
  };
  onCancel?: () => void;
  onDiscardAiReview?: () => void;
  onSuccess?: () => void;
}

const pracelItemValidationSchema = Yup.object({
  category: Yup.string()
    .required()
    .min(3)
    .test(
      'category-is-not-prohibited',
      'parcel_item_category_prohibited',
      (_, context) => !context.parent.isCategoryProhibited
    ),
  quantity: Yup.number()
    .min(1, 'only_numbers')
    .max(1000000, 'only_numbers') // Will be later replaced with maxQuantity from backend
    .required('this_field_cannot_be_empty')
    .integer('only_numbers')
    .typeError('only_numbers'),
  declaredValue: Yup.number()
    .min(0.01, 'this_field_cannot_be_empty')
    .required('this_field_cannot_be_empty')
    .max(10 ** 8 - 0.1, 'only_numbers')
    .typeError('only_numbers'),
  imei: Yup.string().when('foundHsCode', {
    is: (hsCode: HsCodeResponse | undefined) =>
      hsCode?.additional_fields?.includes('imei'),
    then: (imei) =>
      imei
        .required('this_field_cannot_be_empty')
        .min(15, 'UZ_IMEI_validation_error')
        .max(15, 'UZ_IMEI_validation_error'),
    otherwise: (imei) => imei.strip(),
  }),
  descriptionFree: Yup.string().notRequired(),
});

const FormEffects = () => {
  const { setFieldValue, values } = useFormikContext<ParcelItemFormValues>();

  useEffect(() => {
    if (values.foundHsCode?.additional_fields?.includes('imei')) {
      setFieldValue('quantity', 1, true);
    }
  }, [values.foundHsCode, setFieldValue]);

  return undefined;
};

const ParcelItemEditForm = ({
  parcelItem,
  displayCancelButton,
  currencySymbol,
  defaultValues,
  allowUnmodifiedSubmit = false,
  aiReviewProgress,
  onCancel,
  onDiscardAiReview,
  onSuccess,
}: Props) => {
  const {
    parcelCreationStore: {
      setIsNextButtonClicked,
      isNextButtonClicked,
      shipment,
      addItemToShipment,
      modifyItemInShipment,
    },
  } = useStore();

  const { closeEditForm, setModified } = useEditForm();

  const initialValues: ParcelItemFormValues = useMemo(() => {
    if (defaultValues) {
      return {
        ...defaultValues,
        is_new: defaultValues.is_new ?? true,
      };
    }

    return {
      foundHsCode: parcelItem ? new HsCodeResponse(parcelItem) : undefined,
      category: parcelItem?.description_standart ?? '',
      quantity: parcelItem?.number_of_items ?? 1,
      declaredValue: parcelItem?.value,
      imei: parcelItem?.additional_fields?.imei?.at(0),
      descriptionFree: parcelItem?.description_free,
      is_ai_generated: parcelItem?.is_ai_generated ?? !!aiReviewProgress,
      is_new: parcelItem?.is_new ?? true,
    };
  }, [parcelItem, defaultValues, aiReviewProgress]);

  const { t } = useTranslation();

  const handleFormSubmit = async (
    formValues: ParcelItemFormValues,
    formikHelpers: FormikHelpers<ParcelItemFormValues>
  ) => {
    const newParcelItem = {
      id: 0,
      category_id: formValues.foundHsCode?.category_id ?? '',
      hs_code: formValues.foundHsCode?.hsCode,
      description_standart: formValues.category,
      description_standart_en: formValues.foundHsCode?.descriptionEn ?? '',
      description_free: formValues.descriptionFree,
      number_of_items: formValues.quantity,
      value: formValues.declaredValue!,
      is_ai_generated: formValues.is_ai_generated ?? !!aiReviewProgress,
      is_new: formValues.is_new,
    } as ParcelItem;

    if (formValues.imei) {
      newParcelItem.additional_fields = { imei: [formValues.imei] };
    }

    let declaredValueError: string | undefined;

    if (parcelItem) {
      // take existing id, which is required for update operation
      newParcelItem.id = parcelItem.id;
      declaredValueError = await modifyItemInShipment(newParcelItem!);
    } else {
      declaredValueError = await addItemToShipment(newParcelItem!);
    }

    if (declaredValueError) {
      formikHelpers.setFieldError('declaredValue', declaredValueError);
      return;
    }

    setModified(false);

    if (onSuccess) {
      onSuccess();
    } else {
      closeEditForm();
    }
  };

  const handleCancel = () => {
    setModified(false);

    if (onCancel) {
      onCancel();
    } else {
      closeEditForm();
    }
  };

  const handleDiscardAiReview = () => {
    setModified(false);
    onDiscardAiReview?.();
  };

  return (
    <ParcelItemEditFormContainer>
      <Formik
        validationSchema={pracelItemValidationSchema}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}>
        {({
          submitForm,
          setFieldValue,
          values,
          dirty,
          isValid,
          isSubmitting,
        }) => {
          const hasValuesChanged =
            JSON.stringify(values) !== JSON.stringify(initialValues);

          setModified(hasValuesChanged);

          const triggerFieldValidation =
            isNextButtonClicked.value && !shipment?.items?.length;

          const showSaveChangesError = triggerFieldValidation && isValid;

          const showImeiField =
            values.foundHsCode?.additional_fields?.includes('imei');

          return (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitForm();
              }}>
              <FormInputs>
                <div>
                  <GatewayCategorySearch />

                  <FormFieldText
                    name='quantity'
                    disabled={showImeiField}
                    placeholder='1'
                    minValue={1}
                    label={t('quantity')}
                    inputMode='numeric'
                    onChange={() => {
                      setIsNextButtonClicked(false);
                    }}
                    triggerValidation={triggerFieldValidation}
                    incrementControls
                  />
                </div>

                <div>
                  {showImeiField && (
                    <FormFieldText
                      name='imei'
                      placeholder='UZ_IMEI_placeholder'
                      label={t('UZ_IMEI_title')}
                      hint={t('UZ_IMEI_info_text')}
                      maxLength={15}
                      inputMode='numeric'
                      onChange={() => {
                        setIsNextButtonClicked(false);
                      }}
                      triggerValidation={triggerFieldValidation}
                    />
                  )}
                  <FormFieldText
                    name='declaredValue'
                    placeholder=''
                    label={t('declared_value')}
                    infoText={currencySymbol}
                    inputMode='decimal'
                    onChange={() => {
                      setIsNextButtonClicked(false);
                    }}
                    triggerValidation={triggerFieldValidation}
                  />
                </div>
                <FormFieldText
                  name='descriptionFree'
                  placeholder=''
                  label={t('parcel_content_description_label')}
                  isOptional
                  maxLength={100}
                  showMaxLengthCounter
                  inputMode='text'
                  showInfoMessage
                  infoMessageType='infoNoIcon'
                  infoMessage={t('parcel_content_description_hint')}
                  onChange={() => {
                    setIsNextButtonClicked(false);
                  }}
                  triggerValidation={triggerFieldValidation}
                />
                <ConditionSelector aria-label={t('conditions')}>
                  <ConditionOption $selected={values.is_new === true}>
                    <input
                      type='radio'
                      name='is_new'
                      checked={values.is_new === true}
                      onChange={() => {
                        setFieldValue('is_new', true);
                        setIsNextButtonClicked(false);
                      }}
                    />
                    {t('new')}
                  </ConditionOption>
                  <ConditionOption $selected={values.is_new === false}>
                    <input
                      type='radio'
                      name='is_new'
                      checked={values.is_new === false}
                      onChange={() => {
                        setFieldValue('is_new', false);
                        setIsNextButtonClicked(false);
                      }}
                    />
                    {t('used')}
                  </ConditionOption>
                </ConditionSelector>
                {showSaveChangesError && (
                  <InputMessage type='error'>
                    {t('please_save_changes')}
                  </InputMessage>
                )}
              </FormInputs>

              {aiReviewProgress ? (
                <AiReviewControls>
                  <progress
                    className='ai-review-progress'
                    max={aiReviewProgress.total}
                    value={aiReviewProgress.current}
                    aria-label={t(
                      'parcel_items_ai_review_progress',
                      aiReviewProgress
                    )}
                  />
                  <div className='ai-review-progress-label'>
                    {`${aiReviewProgress.current}/${aiReviewProgress.total} ${t('parcel_items_ai_items')}`}
                  </div>
                  <div className='ai-review-buttons'>
                    <Button
                      appearance='text'
                      fullWidth={false}
                      fullWidthMobile={false}
                      disabled={isSubmitting}
                      onClick={handleDiscardAiReview}>
                      {t('cancel')}
                    </Button>
                    <div className='ai-review-actions'>
                      <Button
                        appearance='secondary'
                        disabled={isSubmitting}
                        onClick={handleCancel}>
                        {t('skip')}
                      </Button>
                      <Button
                        type='submit'
                        disabled={
                          (!dirty && !allowUnmodifiedSubmit) ||
                          values.categoryElement?.prohibited ||
                          !isValid ||
                          isSubmitting
                        }
                        isLoading={isSubmitting}>
                        {t('add')}
                      </Button>
                    </div>
                  </div>
                </AiReviewControls>
              ) : (
                <FormBtnsBlock $editMode={displayCancelButton}>
                  {displayCancelButton && (
                    <Button
                      appearance='secondary'
                      onClick={handleCancel}>
                      {t('cancel')}
                    </Button>
                  )}
                  <Button
                    type='submit'
                    disabled={
                      (!dirty && !allowUnmodifiedSubmit) ||
                      !isValid ||
                      isSubmitting
                    }
                    isLoading={isSubmitting}>
                    {!parcelItem ? t('add') : t('save_changes')}
                  </Button>
                </FormBtnsBlock>
              )}
              <FormEffects />
            </form>
          );
        }}
      </Formik>
    </ParcelItemEditFormContainer>
  );
};

export default observer(ParcelItemEditForm);
